import {
  EMPTY_ARRAY,
  getAgGridColId,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type DateParams,
  type FixingIndexMarkMessageData,
} from '@talos/kyoko';
import { useMemo } from 'react';

interface UseIndexMarksColumnsProps {
  defaultColumns?: (keyof FixingIndexMarkMessageData | Partial<Column>)[];
}

const DEFAULT_COLUMNS: (keyof FixingIndexMarkMessageData | Partial<Column>)[] = [
  'Timestamp',
  'NominalTime',
  { id: 'NominalTimeInIndexTZ' },
  'Index',
  'Mark',
  'Source',
  'OrgID',
  'Revision',
  'UpdateAction',
  'ID',
];

export function useIndexMarksColumns({ defaultColumns = DEFAULT_COLUMNS }: UseIndexMarksColumnsProps) {
  const columnDefinitions = useMemo(
    () =>
      new Map<string, ColumnDef<FixingIndexMarkMessageData>>(
        (
          [
            { field: 'Timestamp', type: 'date', title: 'Timestamp', initialSort: '-' },
            {
              id: 'NominalTime',
              field: 'NominalTime',
              type: 'date',
              title: 'Nominal Time (local)',
            },
            {
              id: 'NominalTimeInIndexTZ',
              field: 'NominalTime',
              type: 'date',
              title: 'Nominal Time (Index TZ)',
              initialWidth: 170,
              params: {
                timezone: (data, context) =>
                  data?.Index ? context.current.fixingIndicesByName?.get(data.Index)?.NominalIndexTimeZone : undefined,
              } satisfies DateParams<FixingIndexMarkMessageData>,
            },
            {
              field: 'Index',
              type: 'text',
              title: 'Index Name',
              description: 'The raw index name as received on the IndexMark item.',
            },
            {
              field: 'Index',
              id: 'IndexDisplayName',
              type: 'fixingIndex',
              title: 'Fixing Index',
              description:
                'Resolved from the Fixing Index feed. When the Index matches a known FixingIndex, shows its DisplayName; otherwise shows the Index name from the mark.',
              enableRowGroup: true,
            },
            { field: 'Mark', type: 'text', title: 'Mark' },
            { field: 'Source', type: 'text', title: 'Source' },
            { field: 'OrgID', type: 'text', title: 'Org ID', initialHide: true },
            { field: 'Revision', type: 'text', title: 'Revision' },
            { field: 'UpdateAction', type: 'text', title: 'Update Action', initialHide: true },
            { field: 'UpdateUser', type: 'user', title: 'Update User', initialHide: true },
            { field: 'ID', type: 'text', title: 'ID' },
          ] satisfies ColumnDef<FixingIndexMarkMessageData>[]
        ).map(column => [getAgGridColId(column), column])
      ),
    []
  );

  return useDefaultColumns(defaultColumns ?? EMPTY_ARRAY, columnDefinitions);
}
